import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SectionTitle from "@/components/SectionTitle";
import AnalysisModal from "@/components/AnalysisModal";
import { CalendarDays, Sparkles, LogIn, Coins, FileText, Percent, TrendingUp, AlertTriangle, Zap } from "lucide-react";
import { motion } from "framer-motion";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtDate = (s) => {
  if (!s) return "-";
  const d = new Date(s + "T00:00:00");
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const daysTo = (s) => Math.round((new Date(s + "T00:00:00") - new Date(new Date().toDateString())) / 86400000);

export default function CalendarSection() {
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [tab, setTab] = useState("upcoming");
  const [ticker, setTicker] = useState(null);
  const [rights, setRights] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const switchTab = (t) => { setTab(t); setShowAll(false); };
  const moreBtn = (len, tid) => len > 3 && (
    <button onClick={() => setShowAll((v) => !v)} data-testid={tid} className="mt-3 w-full h-9 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] text-sm font-semibold text-accent hover:bg-[var(--surface-hover)] transition-colors">
      {showAll ? "Tampilkan lebih sedikit" : `Selengkapnya (${len - 3} lainnya)`}
    </button>
  );

  useEffect(() => {
    if (!user) { setNeedLogin(true); return; }
    setLoading(true); setNeedLogin(false);
    api.get("/calendar").then((r) => setData(r.data)).catch((e) => { if (e?.response?.status === 401) setNeedLogin(true); }).finally(() => setLoading(false));
    api.get("/rights-issues").then((r) => setRights(r.data.items || [])).catch(() => {});
  }, [user]);

  const upcoming = data?.upcoming || [];
  const recent = data?.recent_dividends || [];

  return (
    <section id="calendar" className="scroll-mt-20">
      <SectionTitle eyebrow="Corporate Action IDX" title="Kalender Emiten" desc="Jadwal ex-dividend, laporan keuangan & rights issue (HMETD) dengan Cum/Ex-Date dan pengingat aksi." icon={CalendarDays} />

      {needLogin ? (
        <div className="card p-12 text-center" data-testid="calendar-login-prompt">
          <Sparkles size={26} className="text-accent mx-auto mb-2" />
          <p className="text-sm text-[var(--text-secondary)] mb-4">Masuk untuk melihat kalender corporate action emiten.</p>
          <button onClick={login} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)]"><LogIn size={16} /> Masuk dengan Google</button>
        </div>
      ) : loading ? (
        <div className="card py-16 text-center"><div className="w-9 h-9 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="text-sm text-[var(--text-secondary)]">Memuat kalender...</p></div>
      ) : (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => switchTab("upcoming")} data-testid="calendar-tab-upcoming" className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === "upcoming" ? "bg-[var(--accent)] text-[#07090E]" : "bg-[var(--surface-base)] border border-[var(--border)] text-[var(--text-secondary)]"}`}>Agenda Mendatang ({upcoming.length})</button>
            <button onClick={() => switchTab("dividends")} data-testid="calendar-tab-dividends" className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === "dividends" ? "bg-[var(--accent)] text-[#07090E]" : "bg-[var(--surface-base)] border border-[var(--border)] text-[var(--text-secondary)]"}`}>Dividen Terbaru ({recent.length})</button>
            <button onClick={() => switchTab("rights")} data-testid="calendar-tab-rights" className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === "rights" ? "bg-[var(--accent)] text-[#07090E]" : "bg-[var(--surface-base)] border border-[var(--border)] text-[var(--text-secondary)]"}`}>Rights Issue ({rights.length})</button>
          </div>

          {tab === "rights" ? (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="calendar-rights">
              {rights.length === 0 ? <p className="text-center text-sm text-[var(--text-muted)] py-10">Belum ada jadwal rights issue.</p> : (showAll ? rights : rights.slice(0, 3)).map((e, i) => (
                <motion.div key={e.id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }} data-testid={`rights-${e.symbol}`} className="p-3 rounded-lg bg-[var(--surface-base)] border border-[var(--border-subtle)]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-[var(--gold)]/12 text-gold"><TrendingUp size={18} /></div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setTicker(e.symbol)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading font-bold text-accent">{e.symbol}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[var(--surface-hover)] text-[var(--text-secondary)]">{e.status}</span>
                        {e.ratio && <span className="text-[10px] text-[var(--text-muted)]">Rasio {e.ratio}</span>}
                        {e.exercise_price != null && <span className="text-[10px] text-[var(--text-muted)]">· Tebus Rp {Number(e.exercise_price).toLocaleString("id-ID")}</span>}
                      </div>
                      <p className="text-[11px] text-[var(--text-muted)] truncate">{e.name}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2.5 text-center">
                    {[["Cum-Date", e.cum_date, e.cum_days], ["Ex-Date", e.ex_date, null], ["Mulai HMETD", e.trading_start, null], ["Akhir Penebusan", e.trading_end, e.end_days]].map(([lbl, dt, dd], k) => (
                      <div key={k} className="rounded-md bg-[var(--surface-card)] border border-[var(--border-subtle)] py-1.5 px-1">
                        <p className="text-[9px] uppercase tracking-wide text-[var(--text-muted)]">{lbl}</p>
                        <p className="font-mono-data text-[11px] font-semibold">{fmtDate(dt)}</p>
                        {dd != null && dd >= 0 && <p className="text-[9px] text-amber">{dd === 0 ? "hari ini" : `H-${dd}`}</p>}
                      </div>
                    ))}
                  </div>
                  {e.reminders?.map((r, k) => (
                    <div key={k} data-testid={`rights-reminder-${e.symbol}-${k}`} className={`flex items-start gap-2 mt-2 p-2 rounded-md text-[11px] ${r.level === "danger" ? "bg-[var(--bear)]/10 text-bear" : "bg-[var(--amber)]/10 text-amber"}`}>
                      {r.level === "danger" ? <Zap size={13} className="mt-0.5 shrink-0" /> : <AlertTriangle size={13} className="mt-0.5 shrink-0" />}
                      <span><b>{r.label} · {r.stage}:</b> <span className="text-[var(--text-secondary)]">{r.action}</span></span>
                    </div>
                  ))}
                  {e.note && <p className="text-[10px] text-[var(--text-muted)] mt-2 italic">{e.note}</p>}
                </motion.div>
              ))}
              {moreBtn(rights.length, "rights-more")}
            </div>
          ) : tab === "upcoming" ? (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="calendar-upcoming">
              {upcoming.length === 0 ? <p className="text-center text-sm text-[var(--text-muted)] py-10">Belum ada agenda mendatang.</p> : (showAll ? upcoming : upcoming.slice(0, 3)).map((e, i) => {
                const isDiv = e.type === "Ex-Dividend";
                const dtl = daysTo(e.date);
                return (
                  <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }} onClick={() => setTicker(e.symbol)} data-testid={`event-${e.symbol}-${i}`} className="flex items-center gap-3 p-3 rounded-lg bg-[var(--surface-base)] border border-[var(--border-subtle)] hover:border-[var(--accent)]/30 cursor-pointer transition-colors">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isDiv ? "bg-[var(--bull)]/12 text-bull" : "bg-[var(--accent)]/12 text-accent"}`}>{isDiv ? <Coins size={18} /> : <FileText size={18} />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-heading font-bold text-accent">{e.symbol}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isDiv ? "bg-[var(--bull)]/15 text-bull" : "bg-[var(--surface-hover)] text-[var(--text-secondary)]"}`}>{e.type}</span>
                      </div>
                      <p className="text-[11px] text-[var(--text-muted)] truncate">{e.name}</p>
                    </div>
                    {isDiv && e.dividend != null && (
                      <div className="text-right shrink-0 hidden sm:block">
                        <p className="font-mono-data font-bold text-sm text-bull">Rp {Number(e.dividend).toLocaleString("id-ID")}</p>
                        {e.div_yield != null && <p className="text-[10px] text-[var(--text-muted)]">yield {e.div_yield}%</p>}
                      </div>
                    )}
                    <div className="text-right shrink-0 w-24">
                      <p className="font-mono-data text-sm font-semibold">{fmtDate(e.date)}</p>
                      <p className="text-[10px] text-amber">{dtl <= 0 ? "hari ini" : `${dtl} hari lagi`}</p>
                    </div>
                  </motion.div>
                );
              })}
              {moreBtn(upcoming.length, "upcoming-more")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[480px] overflow-y-auto pr-1" data-testid="calendar-dividends">
              {recent.length === 0 ? <p className="text-center text-sm text-[var(--text-muted)] py-10 col-span-2">Belum ada data dividen.</p> : (showAll ? recent : recent.slice(0, 3)).map((e, i) => (
                <div key={i} onClick={() => setTicker(e.symbol)} data-testid={`dividend-${e.symbol}`} className="flex items-center gap-3 p-3 rounded-lg bg-[var(--surface-base)] border border-[var(--border-subtle)] hover:border-[var(--accent)]/30 cursor-pointer transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[var(--bull)]/12 text-bull flex items-center justify-center shrink-0"><Coins size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <span className="font-heading font-bold text-accent">{e.symbol}</span>
                    <p className="text-[11px] text-[var(--text-muted)] truncate">Ex-Date {fmtDate(e.date)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono-data font-bold text-sm">Rp {Number(e.dividend).toLocaleString("id-ID")}</p>
                    {e.div_yield != null && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-bull"><Percent size={9} />{e.div_yield} yield</span>}
                  </div>
                </div>
              ))}
              {recent.length > 3 && <div className="col-span-1 sm:col-span-2">{moreBtn(recent.length, "dividends-more")}</div>}
            </div>
          )}
          <p className="text-[10px] text-[var(--text-muted)] mt-3">Sumber estimasi Yahoo Finance · klik untuk Analisis AI. Verifikasi tanggal resmi di keterbukaan informasi IDX. DYOR.</p>
        </div>
      )}
      {ticker && <AnalysisModal ticker={ticker} onClose={() => setTicker(null)} />}
    </section>
  );
}
